import { useRef, useEffect, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Define typed element variables for Three.js components
const GroupElement = 'group' as any;
const MeshElement = 'mesh' as any;
const CircleGeometryElement = 'circleGeometry' as any;
const RingGeometryElement = 'ringGeometry' as any;
const MeshBasicMaterialElement = 'meshBasicMaterial' as any;

interface AvatarProps {
  position?: [number, number, number];
  scale?: number;
}

const Avatar = ({ position = [0, 0, 0], scale = 1 }: AvatarProps) => {
  const avatarRef = useRef<THREE.Group>(null);
  
  // Create a texture from the profile photo
  const texture = new THREE.TextureLoader().load('/profile-photo.jpg');
  
  // Make the texture smooth
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  
  // Create a circular mask for the photo
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide,
  });

  useFrame(({ clock }) => {
    if (!avatarRef.current) return;
    
    // Subtle floating animation
    const t = clock.getElapsedTime();
    avatarRef.current.position.y = position[1] + Math.sin(t) * 0.1;
    
    // Subtle rotation
    avatarRef.current.rotation.y = Math.sin(t * 0.5) * 0.1;
  });

  // Size of the avatar adjusted by scale factor
  const circleRadius = 1.2 * scale;
  const ringInnerRadius = circleRadius;
  const ringOuterRadius = circleRadius + (0.1 * scale);

  return (
    <GroupElement ref={avatarRef} position={position} scale={[scale, scale, scale]}>
      <MeshElement>
        <CircleGeometryElement args={[circleRadius, 32]} />
        <MeshBasicMaterialElement map={texture} side={THREE.DoubleSide} />
      </MeshElement>
      {/* Add a glowing border */}
      <MeshElement>
        <RingGeometryElement args={[ringInnerRadius, ringOuterRadius, 32]} />
        <MeshBasicMaterialElement color="#00ff00" />
      </MeshElement>
    </GroupElement>
  );
};

const AvatarWrapper = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if on mobile
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    mediaQuery.addEventListener('change', handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
    };
  }, []);

  return <Avatar 
    position={isMobile ? [0, -1, 0] : [0, 0, 0]} 
    scale={isMobile ? 1.5 : 1} // Increase scale for mobile to make the avatar appear larger
  />;
};

export default AvatarWrapper;